/**
 * LaunchReveal — C4 in-app animated wordmark reveal.
 *
 * Timing budget: 1150 ms total (under the 1.2 s ceiling).
 *   0-350ms   navy background swells; infinity "nn" outline strokes itself in
 *   350-750ms orange→blue gradient sweep across the wordmark
 *   750-1150ms wordmark fades to full opacity, scales 0.94 → 1, hand-off
 *
 * `prefers-reduced-motion`: shows the static wordmark for ~300 ms, no
 * stroke draw, no gradient sweep, instant handoff.
 *
 * The component self-unmounts by calling `onDone` after animation
 * (parent controls final removal to avoid layout thrash).
 */
import React, { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";

// Vector-only wordmark. L, i, k are pure orange strokes. The infinity
// "nn" mark is the same clipPath used by /assets/link-full-logo.svg
// but rendered as a stroked path (no raster PNG) so we can animate the
// draw with `strokeDasharray` + `pathLength`.
//
// The full source shape is defined in a viewBox 245.93×97.22. We use
// framer-motion's `pathLength` for the stroke draw (framer normalises
// the path length to 0..1 which cross-browser beats manual dashoffset).
function LinkWordmarkVector({ reduced, animateInfinity }) {
  return (
    <svg
      viewBox="0 0 245.93 97.22"
      width="240"
      height="94"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: "block" }}
      data-testid="launch-wordmark-svg"
    >
      <defs>
        <linearGradient id="launchGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#ed6500" />
          <stop offset="100%" stopColor="#4B9CF8" />
        </linearGradient>
      </defs>
      {/* L, i, k — always solid orange */}
      <path fill="#ed6500" d="M0,5.19h24.05v54.68c0,4.27.6,7.11,1.81,8.53,1.2,1.42,3.58,2.12,7.14,2.12h1.25v26.7h-6.4c-8.77,0-15.6-2.91-20.5-8.73-4.9-5.82-7.35-13.9-7.35-24.24V5.19Z" />
      <path fill="#ed6500" d="M63.39,30v67.22h-22.16V30h22.16ZM52.37,0c3,0,5.6,1.2,7.79,3.61,2.19,2.41,3.29,5.25,3.29,8.53,0,3.51-1.03,6.38-3.11,8.63-2.07,2.25-4.73,3.37-7.97,3.37s-5.89-1.12-7.97-3.37c-2.07-2.25-3.11-5.12-3.11-8.63,0-3.28,1.1-6.12,3.29-8.53,2.19-2.4,4.79-3.61,7.79-3.61Z" />
      <path fill="#ed6500" d="M206.71,5.19v92.03h-22.16V5.19h22.16ZM240.66,30l-10.84,24.47,16.11,42.75h-24.53l-13.86-43.89,10.07-23.33h23.04Z" />
      {/* Infinity "nn" mark — vector clipPath path, stroke-drawn */}
      <motion.path
        d="M73.04,87.44c-1.82-3.26-2.46-6.8-2.51-10.46l-.09-6.72.12-35.5c.03-7.93,4.85-14.64,11.66-17.58,4.78-2.06,10.08-2.14,14.9-.25,2.67,1.05,5.08,2.42,7.07,4.53l5.31,5.63,34.49,38,11.62,12.86c1.07,1.18,2.37,1.98,3.95,1.26.85-.39,1.34-1.37,1.34-2.43l-.02-40.22c0-1.49-.7-2.73-2.08-3.1-1.39-.37-2.73.27-3.63,1.48l-14.71,16.44-11.81-12.89,14.6-16.48c6.13-6,14.56-8.29,22.39-5.04,7,2.9,11.91,9.94,11.91,17.99l.02,43.06c0,6.53-3.36,12.53-8.51,15.96-4.78,3.18-10.44,3.89-15.86,2.47-4.37-1.14-7.89-3.76-10.96-7.14l-27.58-30.28-7.09-7.75-15.52-17.17c-.83-.92-2.25-.85-3.25-.47-.85.32-1.95,1.25-1.95,2.56l-.02,40.4c0,1.48.75,2.47,2.1,2.87,1.16.34,2.43-.15,3.32-1.12l15.39-16.98,11.82,12.87-14.86,16.59c-5.73,5.86-13.88,7.96-21.46,5.23-4.22-1.52-7.79-4.46-10.09-8.6Z"
        fill="none"
        stroke="url(#launchGrad)"
        strokeWidth={3}
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={reduced ? { pathLength: 1 } : { pathLength: 0 }}
        animate={reduced ? { pathLength: 1 } : (animateInfinity ? { pathLength: 1 } : { pathLength: 0 })}
        transition={reduced ? { duration: 0 } : { duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        data-testid="launch-infinity-path"
      />
    </svg>
  );
}

export default function LaunchReveal({ onDone }) {
  const reduced = useReducedMotion();
  const [phase, setPhase] = useState(0); // 0: mounted, 1: fill sweep, 2: done

  useEffect(() => {
    // Set the "played" flag IMMEDIATELY on mount so a mid-animation
    // unmount (React strict-mode double-invoke, hot-reload, etc.)
    // still prevents the reveal from replaying in this session.
    try { sessionStorage.setItem("link.launch.played", "1"); } catch (_) { /* noop */ }
    if (reduced) {
      // Static ~300ms then handoff.
      const t = setTimeout(() => onDone?.(), 320);
      return () => clearTimeout(t);
    }
    const t1 = setTimeout(() => setPhase(1), 380);
    const t2 = setTimeout(() => setPhase(2), 1150);
    const t3 = setTimeout(() => onDone?.(), 1200);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [reduced, onDone]);

  return (
    <motion.div
      className="fixed inset-0 z-[9998] flex items-center justify-center"
      style={{ background: "#0A1128" }}
      initial={reduced ? { opacity: 1 } : { opacity: 1 }}
      animate={{ opacity: phase === 2 ? 0 : 1 }}
      transition={reduced ? { duration: 0 } : { duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
      data-testid="launch-reveal"
      data-launch-reduced={reduced ? "true" : "false"}
      data-launch-phase={String(phase)}
      aria-hidden="true"
    >
      <motion.div
        initial={reduced ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.94 }}
        animate={reduced ? { opacity: 1, scale: 1 } : { opacity: 1, scale: 1 }}
        transition={reduced ? { duration: 0 } : { duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
      >
        <LinkWordmarkVector reduced={!!reduced} animateInfinity={phase >= 1} />
      </motion.div>
    </motion.div>
  );
}
